<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">活躍度分析</h1>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <label>活躍定義天數</label>
        <input
          v-model.number="filters.days"
          type="number"
          class="form-control"
          min="1"
          max="365"
        />
      </div>
      <button class="btn btn-primary" @click="fetchData">查詢</button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchData">重試</button>
    </div>

    <div v-else class="dashboard-content">
      <!-- 統計卡片 -->
      <div class="stats-cards">
        <div class="stat-card">
          <div class="stat-label">月活躍用戶數 (MAU)</div>
          <div class="stat-value">{{ mau || 0 }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">日活躍用戶數 (DAU)</div>
          <div class="stat-value">{{ dau || 0 }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">活躍率</div>
          <div class="stat-value">{{ activityRate ? (activityRate.activityRate * 100).toFixed(2) + '%' : '-' }}</div>
        </div>
      </div>

      <!-- 活躍度趨勢圖 -->
      <div v-if="trendData.length > 0" class="chart-section">
        <div class="chart-card">
          <TrendChart
            title="活躍度趨勢"
            :data="trendData"
            xAxisKey="date"
            yAxisKey="activeMembers"
            height="400px"
            color="#4facfe"
          />
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { analyticsService } from '@/api/modules/analytics.js';
import { showError } from '@/utils/swal.js';
import TrendChart from '@/components/charts/TrendChart.vue';

export default {
  name: 'ActivityAnalysis',
  components: {
    TrendChart
  },
  setup() {
    const loading = ref(false);
    const error = ref(null);
    const mau = ref(0);
    const dau = ref(0);
    const activityRate = ref(null);
    const trend = ref([]);
    const filters = ref({
      days: 30
    });

    const trendData = computed(() => {
      return trend.value.map(item => ({
        date: item.date,
        activeMembers: item.activeMembers,
        totalOrders: item.totalOrders,
        totalRevenue: item.totalRevenue
      }));
    });

    const fetchData = async () => {
      try {
        loading.value = true;
        error.value = null;

        // 並行取得所有數據
        const [mauResponse, dauResponse, rateResponse, trendResponse] = await Promise.all([
          analyticsService.getMAU(),
          analyticsService.getDAU(),
          analyticsService.getActivityRate({ days: filters.value.days }),
          analyticsService.getActivityTrend({ days: filters.value.days })
        ]);

        if (mauResponse.success) {
          mau.value = mauResponse.data.mau || 0;
        }

        if (dauResponse.success) {
          dau.value = dauResponse.data.dau || 0;
        }

        if (rateResponse.success) {
          activityRate.value = rateResponse.data;
        }

        if (trendResponse.success) {
          trend.value = trendResponse.data.trend || [];
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '取得資料失敗';
        showError('錯誤', error.value);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchData();
    });

    return {
      loading,
      error,
      mau,
      dau,
      activityRate,
      trend,
      filters,
      trendData,
      fetchData
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.dashboard-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stat-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 32px;
  font-weight: 600;
  color: #333;
}

.chart-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.chart-card {
  width: 100%;
}
</style>
